/* eslint-disable max-lines */
import tokens from '@kluein/design-tokens';
import { Box, Button, Text, Badge, Tip } from '@kluein/klue-ui';
import {
  PaperPlaneRight,
  ToggleLeft,
  ToggleRight,
} from '@phosphor-icons/react';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from 'contexts/auth';
import { useAskKlueLog } from 'hooks/dashboard/use-ask-klue-log';
import { AnalyticsAction } from 'lib/analytics';
import { SourceFilterButton } from 'modules/shared/libs/chat/components/SourceFilterButton';
import { ChatStatusHelpers } from 'modules/shared/libs/chat/constants';
import { useEffectivePreviewingVisibilityGroup } from 'pages/dashboard/hooks';
import { setAskKlueResearchMode, useAskKlueResearchMode } from 'store/views';
import TEST_IDS from 'test-ids';
import TRACKING_IDS from 'tracking-ids';

import { StyledAskKlueBadge, StyledInput } from './AskKlueInput.styles';
import AskKlueInputDashboard from './partials/ask-klue-input-dashboard/AskKlueInputDashboard';

import ProfileLibrary from '../profile-library/ProfileLibrary';
import PromptLibrary from '../prompt-library/PromptLibrary';

import type { UseAnswerChatReturnType } from 'modules/current-compete/libs/ask-klue-answer-mode-chat/hooks/useAnswerChat';
import type { UseResearchChatReturnType } from 'modules/current-compete/libs/ask-klue-research-mode-chat/hooks/useResearchChat';
import type { Source } from 'modules/shared/api/sources.api.types';

export type AskKlueInputProps = {
  isModal?: boolean;
  isPage?: boolean;
  isResponseLoading?: boolean;
  isClickableButton?: boolean;
  isResearchModeClickable?: boolean;
  isInitalScreen?: boolean;
  handleAskKlue?: (question: string) => void;
  // When the typed input matches a supported rival, opens the rival profile
  // and returns true so the caller can skip the chat-submit. handleAskKlue
  // embeds this check; sending straight to activeChat needs it explicitly.
  tryOpenSupportedRival?: (input: string) => boolean;
  handleClickableButton?: () => void;
  activeChat?: UseResearchChatReturnType | UseAnswerChatReturnType;
  disabled?: boolean;
  modernSources?: Source[];
  modernDisabledSources?: string[];
  onModernDisabledSourcesChange?: (disabled: string[]) => void;
  isLoadingModernSources?: boolean;
  consumerSourcesLocked?: boolean;
  onDidSubmitAskKlue?: () => void;
};

const colors = {
  purple: {
    backgroundColor: tokens.color.primitives.violet[75],
    borderColor: tokens.color.primitives.violet[300],
    textColor: tokens.color.primitives.violet[500],
    iconColor: tokens.color.primitives.violet[450],
  },
  grey: {
    backgroundColor: tokens.color.primitives.white,
    borderColor: tokens.color.primitives.gray[150],
    textColor: tokens.color.primitives.gray[425],
    iconColor: tokens.color.primitives.gray[200],
  },
};

function AskKlueResearchModeBadge({
  color = 'grey',
  onClick,
}: {
  color?: 'grey' | 'purple';
  onClick?: () => void;
}) {
  const { t } = useTranslation('Dashboard');
  const { backgroundColor, borderColor, textColor, iconColor } = colors[color];
  const BadgeIcon = color === 'purple' ? ToggleRight : ToggleLeft;

  return (
    <Tip
      content={t('Dashboard:askKlue.answerModal.researchModeTip')}
      dropProps={{ align: { top: 'bottom' } }}
      color="dark"
      variant="small"
    >
      <StyledAskKlueBadge
        direction="row"
        align="center"
        gap="xxsmall"
        $borderColor={borderColor}
        $backgroundColor={backgroundColor}
        $isClickable={!!onClick}
        flex={{ shrink: 0 }}
        round={tokens.borderRadius.small}
        background={backgroundColor}
        pad={{ horizontal: 'xxsmall' }}
        onClick={() => onClick?.()}
      >
        <BadgeIcon
          size="20px"
          format="outline"
          color={iconColor}
          weight="fill"
        />
        <Text
          fontWeight="semibold"
          letterSpacing="xsmall"
          size="small"
          lineHeight="small"
          color={textColor}
        >
          {t('Dashboard:askKlue.inputText.labelCheckBox')}
        </Text>
      </StyledAskKlueBadge>
    </Tip>
  );
}

function AskKlueInput({
  isModal,
  isPage,
  isResponseLoading,
  handleAskKlue,
  tryOpenSupportedRival,
  isClickableButton,
  isResearchModeClickable,
  handleClickableButton,
  isInitalScreen,
  activeChat,
  disabled,
  modernSources,
  modernDisabledSources,
  onModernDisabledSourcesChange,
  isLoadingModernSources,
  consumerSourcesLocked,
  onDidSubmitAskKlue,
}: AskKlueInputProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState<string>('');
  const [inputHeight, setInputHeight] = useState<number | null>(null);
  const askKlueInputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(
    null,
  );

  const { isCurator, isPromptLibraryEnabled, isCompanyLandingPageEnabled } =
    useAuth();
  // Curators in any flavor of view-as (general `?previewing=<id>` OR
  // AskKlue-specific `?askKlueVisibilityGroupId=<id>`) should see the
  // consumer dropdown. `isCurator` only flips for the former, so check the
  // effective preview ID for the latter.
  const previewingVisibilityGroup = useEffectivePreviewingVisibilityGroup();
  const logAskKlueEvent = useAskKlueLog();

  const isResearchMode = useAskKlueResearchMode();

  const loading = activeChat
    ? ChatStatusHelpers.isProcessing(activeChat.status)
    : isResponseLoading;

  // Combined state for disabling all interactive elements
  const isDisabled = disabled || loading;

  // eslint-disable-next-line no-nested-ternary
  const placeholder = isPage
    ? 'placeholderPage'
    : isInitalScreen
      ? 'placeholderInitialScreen'
      : 'placeholder';

  const setInitialInputHeight = (forceReset = false) => {
    if (askKlueInputRef.current) {
      // Reset height if prompt library is disabled, no custom height is set, or force reset is true
      if (!isPromptLibraryEnabled || !inputHeight || forceReset) {
        askKlueInputRef.current.style.height = 'auto';
      }
    }
  };

  // Track manual resizing by the user when prompt library is enabled
  useEffect(() => {
    const input = askKlueInputRef.current as HTMLTextAreaElement;
    if (!input || !isPromptLibraryEnabled) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { height } = entry.contentRect;
        // Only update if height is greater than the default and appears to be user-initiated
        if (height > 0) {
          setInputHeight(height);
        }
      }
    });

    resizeObserver.observe(input);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isPromptLibraryEnabled]);

  const onAskKlueClick = () => {
    if (!Boolean(inputValue) && isModal) {
      return askKlueInputRef.current?.focus();
    }

    // Inside the chat (research or answer), send straight to the active chat.
    // Outside it (the dashboard strip has no activeChat), hand the question
    // to handleAskKlue, which opens the chat and queues it.
    if (activeChat) {
      // Typing an exact supported-rival name opens the rival profile instead
      // of submitting the question. handleAskKlue embeds this check; sending
      // straight to activeChat bypasses it.
      if (tryOpenSupportedRival?.(inputValue)) {
        setInputValue('');
        setInputHeight(null);
        setInitialInputHeight(true);
        return;
      }
      // Sending straight to activeChat bypasses handleAskKlue, where the `ask`
      // event is logged. Emit it here so typed-question engagement remains
      // visible in Ask Klue analytics.
      logAskKlueEvent({
        askKlueId: '',
        eventType: AnalyticsAction.ask,
        text: inputValue,
      });
      activeChat.sendMessage(inputValue);
      onDidSubmitAskKlue?.();
    } else {
      handleAskKlue?.(inputValue);
    }

    setInputValue('');
    setInputHeight(null); // Reset to default size when submitting
    setInitialInputHeight(true); // Force reset to default height
  };

  const onTextInputKeyPress = (
    event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    if (
      isDisabled ||
      inputValue === '' ||
      (inputValue && inputValue?.length < 3) ||
      !(inputValue?.search(/[^\n\s]/) != -1)
    )
      return;

    const { key, shiftKey } = event;
    if (key === 'Enter' && shiftKey) {
      return;
    }

    if (key === 'Enter') {
      event.preventDefault();
      onAskKlueClick();
    }
  };

  const adjustHeight = () => {
    if (askKlueInputRef.current && isModal) {
      askKlueInputRef.current.style.height = 'auto';
      askKlueInputRef.current.style.height = `${askKlueInputRef.current.scrollHeight}px`;
    }
  };

  const onChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const { value } = event.target;
    setInputValue(value);
    adjustHeight();
  };

  const RMBadge = isResearchMode ? (
    <Badge
      color="purple"
      emphasis="medium"
      label={t('Dashboard:askKlue.inputText.labelCheckBox')}
      size="medium"
    />
  ) : null;

  if (!isModal && !isPage) {
    return (
      <AskKlueInputDashboard
        onClick={() => {
          // Blur input before opening modal to prevent focus conflicts
          askKlueInputRef.current?.blur();

          if (inputValue) {
            onAskKlueClick();
          }
          if (isClickableButton && handleClickableButton) {
            handleClickableButton();
          }
        }}
        onChange={onChange}
        inputValue={inputValue}
        onTextInputKeyPress={onTextInputKeyPress}
        isClickableButton={isClickableButton}
      />
    );
  }

  return (
    <Box
      align="center"
      background={tokens.color.neutral.white.main}
      direction="row"
      fill="horizontal"
      pad={{
        left: 'small',
        right: 'large',
        vertical: 'small',
      }}
      round="xsmall"
      justify="between"
      elevation="xsmall"
      border={{ color: tokens.color.primitives.violet[100] }}
      data-test-id={TEST_IDS.askKlue.inputContainer}
      height={{ min: tokens.spacing.large, max: tokens.size.xxxlarge }}
      width="full"
      flex={{ grow: 1 }}
      id="ask-klue-input-container"
      style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
    >
      <Box align="center" direction="column" flex={{ grow: 1 }} gap="xsmall">
        <StyledInput
          size="medium"
          rows={isInitalScreen || isPage ? 2 : 1}
          placeholder={t(`Dashboard:askKlue.${placeholder}`)}
          onChange={onChange}
          onKeyDown={onTextInputKeyPress}
          value={inputValue}
          ref={askKlueInputRef as React.RefObject<HTMLTextAreaElement>}
          data-test-id={TEST_IDS.askKlue.textInput}
          data-tracking-id={TRACKING_IDS.askKlue.askKlueInput}
          disabled={isDisabled}
          $isResizable={isPromptLibraryEnabled}
          $preservedHeight={isPromptLibraryEnabled ? inputHeight : null}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={true}
        />
        <Box direction="row" width="full" justify="between" flex={false}>
          <Box direction="row" align="center" gap="xsmall">
            {isPromptLibraryEnabled && (
              <PromptLibrary
                onPromptSelect={(prompt) => {
                  setInputValue(prompt);
                  setInitialInputHeight();
                }}
              />
            )}
            {isCompanyLandingPageEnabled && (
              <ProfileLibrary
                disabled={isDisabled}
                onProfileSelect={(rivalName) => {
                  // Route through the modern chat when it's active so picks
                  // from the profile library go to the modern backend. Falls
                  // back to handleAskKlue otherwise.
                  if (activeChat) {
                    if (tryOpenSupportedRival?.(rivalName)) return;
                    // Mirror the typed-submit branch: log the ask event
                    // explicitly since handleAskKlue (which normally emits
                    // it) is bypassed here.
                    logAskKlueEvent({
                      askKlueId: '',
                      eventType: AnalyticsAction.ask,
                      text: rivalName,
                    });
                    activeChat.sendMessage(rivalName);
                    onDidSubmitAskKlue?.();
                    return;
                  }
                  handleAskKlue?.(rivalName);
                }}
              />
            )}
            {isCurator && (
              <>
                {isResearchModeClickable ? (
                  <AskKlueResearchModeBadge
                    color={isResearchMode ? 'purple' : 'grey'}
                    onClick={() => setAskKlueResearchMode(!isResearchMode)}
                  />
                ) : (
                  RMBadge
                )}
              </>
            )}
            {activeChat &&
              (modernSources?.length ?? 0) > 0 &&
              (!isCurator ||
                isResearchMode ||
                previewingVisibilityGroup != null) &&
              !consumerSourcesLocked && (
                <Box margin={{ left: 'xsmall' }}>
                  <SourceFilterButton
                    sources={modernSources ?? []}
                    disabledSources={modernDisabledSources ?? []}
                    onDisabledSourcesChange={
                      onModernDisabledSourcesChange ?? (() => {})
                    }
                    disabled={isDisabled}
                    isLoading={isLoadingModernSources}
                    size="xs"
                  />
                </Box>
              )}
          </Box>
          <Button
            size="small"
            icon={
              <PaperPlaneRight
                color={tokens.color.neutral[100]}
                size={tokens.iconSize.small}
              />
            }
            variant="primary"
            truncate={false}
            onClick={onAskKlueClick}
            disabled={isDisabled}
            loading={loading}
            active={true}
            data-test-id={TEST_IDS.askKlue.button}
            data-tracking-id={TRACKING_IDS.askKlue.askKlueButton}
          >
            Ask Klue
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default AskKlueInput;
